import type { Services } from '~/services/types'
import { Logic } from '~common/ui-logic/logic'
import { executeTask, TaskState } from '~common/ui-logic/tasks'
import { AppContextType } from '~/app-context'
import { OFFERS, type Offer } from '~common/offers'
import {
    DEFAULT_PLANS,
    DEFAULT_PLANS_PROD,
} from '~common/payment-processing/constants'

export interface PricingComponentDependencies {
    onClose: () => void
    // onError?: (error: string) => void
    titleId?: string
    isWaitListOnboarding?: boolean
    email?: string
}

export type PackageOption = {
    id: string
    label: string
    invoices: number
    price: number
    lemonsqueezyProductId: number
}

export type PricingTitles = {
    title: string
    subtitle?: string
    showCountdown?: boolean
    discountBannerText?: string
    hasOffer?: boolean
}

export type PricingComponentState = {
    loadState: TaskState
    packages?: PackageOption[]
    selectedPackageId?: string
    error: string | null
    offerEndsAt: number | null
    offerCountdownText: string
    titles: PricingTitles
    showCountdown: boolean
    isUpgrading?: string
    discountBannerText?: string
    hasOffer?: boolean
}

export class PricingComponentLogic extends Logic<
    PricingComponentDependencies & AppContextType,
    PricingComponentState
> {
    getInitialState = (): PricingComponentState => {
        const titleId = this.deps.titleId || 'default'
        const titles = this.getTitles(titleId)
        return {
            loadState: 'pristine',
            packages: undefined,
            selectedPackageId: undefined,
            error: null,
            offerEndsAt: null,
            offerCountdownText: '',
            titles,
            showCountdown: !!titles.showCountdown,
            isUpgrading: undefined,
            discountBannerText: titles.discountBannerText,
            hasOffer: titles.hasOffer,
        }
    }

    async initialize() {
        await executeTask(this, 'loadState', async () => {
            let updatedState = { ...this.state }

            const createdAt =
                this.deps.services.cache.getKey('authState').user?.metadata
                    .creationTime

            if (createdAt) {
                const baseline = new Date(createdAt).getTime()
                const accountAgeMs = Date.now() - baseline

                if (accountAgeMs < 24 * 60 * 60 * 1000) {
                    const offerEndsAt = baseline + 24 * 60 * 60 * 1000
                    updatedState = {
                        ...updatedState,
                        offerEndsAt,
                        showCountdown: true,
                        titles: {
                            ...updatedState.titles,
                            showCountdown: true,
                        },
                    }
                    this.startCountdown(offerEndsAt)
                } else {
                    // Account is older than 24 hours, hide countdown
                    updatedState = {
                        ...updatedState,
                        titles: {
                            ...updatedState.titles,
                            showCountdown: false,
                        },
                        showCountdown: false,
                        offerEndsAt: null,
                        offerCountdownText: '',
                    }
                }
            } else {
                // No creation time available (e.g., emulator or auth issue), hide countdown
                console.warn(
                    'User creation time not available; hiding offer countdown.',
                )
                updatedState = {
                    ...updatedState,
                    titles: {
                        ...updatedState.titles,
                        showCountdown: false,
                    },
                    showCountdown: false,
                    offerEndsAt: null,
                    offerCountdownText: '',
                }
            }

            const isProd = process.env.NODE_ENV === 'production'

            const offeredPricing =
                this.deps.services?.cache.getKey('offeredPricing') ??
                (isProd
                    ? DEFAULT_PLANS_PROD['launch-price']
                    : DEFAULT_PLANS['launch-price'])

            const packages = offeredPricing?.variants.map((variant) => ({
                id: variant.id,
                label: variant.title,
                invoices: variant.limits?.maxInvoicesIngested || 0,
                price: variant.price || 0,
                lemonsqueezyProductId: variant.lemonsqueezyProductId,
            }))

            this.setState({ ...updatedState, packages })
        })
    }
    private getTitles(titleId: string): PricingTitles {
        if (titleId === 'limit-25-hit') {
            return {
                title: 'Automatic Import paused because you have only 25 invoices left',
                subtitle:
                    'This way you can still search and import individual emails',
            }
        }

        const offer = OFFERS[titleId as keyof typeof OFFERS] || OFFERS.default
        return {
            title: offer.copy.pricingmodal.title,
            subtitle: offer.copy.pricingmodal.subtitle,
            showCountdown: offer.showCountdown,
            discountBannerText: offer.copy.pricingmodal.discountBannerText,
            hasOffer: offer.hasOffer && offer.hasOffer?.length > 0,
        }
    }

    private startCountdown(offerEndsAt: number) {
        const tick = () => {
            const remainingMs = Math.max(0, offerEndsAt - Date.now())
            const hours = Math.floor(remainingMs / (1000 * 60 * 60))
            const minutes = Math.floor(
                (remainingMs % (1000 * 60 * 60)) / (1000 * 60),
            )
            const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000)
            const text = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
            this.setState({ ...this.state, offerCountdownText: text })
        }
        const interval = setInterval(tick, 1000)
        tick()
        this.cleanupFns.push(() => clearInterval(interval))
    }

    selectPackage = (packageId: string) => {
        this.setState({
            selectedPackageId: packageId,
        })
        this.startUpgrade(packageId)
    }

    getSelectedPackage = (): PackageOption | null => {
        return (
            this.state.packages?.find(
                (p) => p.id === this.state.selectedPackageId,
            ) || null
        )
    }

    getCtaLabel = () => {
        const selectedPrice = this.getSelectedPackage()?.price
        return this.state.selectedPackageId === 'free'
            ? 'Start Free Trial'
            : `Upgrade for $${selectedPrice}`
    }

    private channel?: BroadcastChannel
    private handleMessage = async (event: MessageEvent) => {
        if (event.data.type === 'checkout-successful') {
            await this.deps.services.cache.refreshEntitlement()
            this.deps.onClose()
        }
    }

    startUpgrade = async (packageId: string) => {
        try {
            if (this.state.selectedPackageId === 'free') {
                this.deps.onClose()
                return
            }
            this.setState({
                isUpgrading: packageId,
            })
            const selected = this.getSelectedPackage()
            if (!selected?.lemonsqueezyProductId) {
                this.setState({
                    ...this.state,
                    error: 'Please select a valid package',
                })
                return
            }

            const userEmail =
                this.deps.email ??
                this.deps.services.cache.getKey('authState')?.user?.email

            if (!userEmail) {
                this.setState({
                    ...this.state,
                    error: 'Please login to proceed with purchase',
                })
                return
            }

            const checkoutUrl =
                await this.deps.services.entitlements.createCheckout(
                    selected.lemonsqueezyProductId,
                    userEmail,
                )
            this.setState({
                isUpgrading: undefined,
            })
            if (typeof window !== 'undefined') {
                window.open(checkoutUrl, '_blank')
            }
            // Spawn listener only for paid upgrades
            if (this.state.isUpgrading === 'success') {
                this.channel = new BroadcastChannel('taxing-checkout')
                this.channel.addEventListener('message', this.handleMessage)
            }
        } catch (error) {
            console.error('Failed to create checkout:', error)
            this.setState({
                ...this.state,
                error: 'Failed to initiate purchase. Please try again.',
            })
        }

        // this.deps.onClose()
    }
}
